import { z } from 'zod';

export const SharedDataScopeSchema = z.object({
  partnerDwKey: z.string().min(1),
  namespace: z.string().min(1),
});
export const SharedDataWriteSchema = SharedDataScopeSchema.extend({
  schemaVersion: z.number().int().positive(),
  data: z.json(),
});
export const SharedDataRecordSchema = SharedDataWriteSchema.extend({
  id: z.uuid(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export const SharedDataIdSchema = SharedDataScopeSchema.extend({ id: z.uuid() });
export type SharedDataRecord = z.infer<typeof SharedDataRecordSchema>;

export const SharedDataRequestSchema = z.discriminatedUnion('operation', [
  SharedDataScopeSchema.extend({ operation: z.literal('list') }),
  SharedDataIdSchema.extend({ operation: z.literal('get') }),
  z.object({
    operation: z.literal('save'),
    record: SharedDataRecordSchema,
    create: z.boolean(),
  }),
  SharedDataIdSchema.extend({ operation: z.literal('remove') }),
]);
export type SharedDataRequest = z.infer<typeof SharedDataRequestSchema>;
export const SharedDataInvocationSchema = z.object({
  accessToken: z.string().min(1),
  request: SharedDataRequestSchema,
});
const accessFailures = [
  z.strictObject({ status: z.literal('unauthorized') }),
  z.strictObject({ status: z.literal('forbidden') }),
] as const;
const notFound = z.strictObject({ status: z.literal('not-found') });
const conflict = z.strictObject({ status: z.literal('conflict') });
const mutationSuccess = z.strictObject({ status: z.literal('ok') });

export const SharedDataListResponseSchema = z.discriminatedUnion('status', [
  z.strictObject({ status: z.literal('ok'), records: z.array(SharedDataRecordSchema) }),
  ...accessFailures,
]);
export const SharedDataGetResponseSchema = z.discriminatedUnion('status', [
  z.strictObject({ status: z.literal('ok'), record: SharedDataRecordSchema }),
  notFound,
  ...accessFailures,
]);
export const SharedDataSaveResponseSchema = z.discriminatedUnion('status', [
  mutationSuccess,
  notFound,
  conflict,
  ...accessFailures,
]);
export const SharedDataRemoveResponseSchema = z.discriminatedUnion('status', [
  mutationSuccess,
  notFound,
  ...accessFailures,
]);
export type SharedDataResponse =
  | z.infer<typeof SharedDataListResponseSchema>
  | z.infer<typeof SharedDataGetResponseSchema>
  | z.infer<typeof SharedDataSaveResponseSchema>
  | z.infer<typeof SharedDataRemoveResponseSchema>;
